import { useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

function loadScript() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
    return new Promise((resolve) => {
      const check = () => (window.turnstile ? resolve() : setTimeout(check, 50));
      check();
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Turnstile'));
    document.head.appendChild(s);
  });
}

export default function CaptchaWidget({ onToken, theme = 'light' }) {
  const ref = useRef(null);
  const widgetRef = useRef(null);
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey || !ref.current) return;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return;
        widgetRef.current = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => onToken?.(token),
          'expired-callback': () => onToken?.(''),
          'error-callback': () => onToken?.(''),
        });
      })
      .catch((e) => console.warn('[captcha] load error:', e));

    return () => {
      cancelled = true;
      if (widgetRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetRef.current);
        } catch {
          /* ignore */
        }
      }
    };
  }, [siteKey, theme, onToken]);

  if (!siteKey) {
    return (
      <div
        style={{
          padding: '8px 12px',
          background: '#fef3c7',
          color: '#92400e',
          borderRadius: 8,
          fontSize: 12.5,
        }}
      >
        Captcha disabled — set <code>VITE_TURNSTILE_SITE_KEY</code> to enable.
      </div>
    );
  }

  return <div ref={ref} style={{ display: 'flex', justifyContent: 'flex-start' }} />;
}
